import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button } from '@/components/ui/button';

interface OrdersDrilldownPaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

/** Footer pinned to the bottom of the sheet: "21–40 of 134" + prev/next. */
export function OrdersDrilldownPagination({
  page,
  pageSize,
  total,
  onPageChange,
}: OrdersDrilldownPaginationProps) {
  const { LL, locale } = useI18nContext();
  if (total <= 0) return null;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const format = (n: number) => new Intl.NumberFormat(locale).format(n);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {LL.metricsPage.repurchaseDrilldown.paginationRange({
          from: format(from),
          to: format(to),
          total: format(total),
        })}
      </p>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8"
          disabled={page <= 1}
          aria-label={LL.metricsPage.repurchaseDrilldown.previousPage()}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="px-2 text-sm text-muted-foreground">
          {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8"
          disabled={page >= totalPages}
          aria-label={LL.metricsPage.repurchaseDrilldown.nextPage()}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}
